import { EntityHandler, EntityListResult, SectionHandlerGroup, FormSectionHandler } from '../entity.handler';
import { IKuiTableCol } from 'app/key-ui';
import { Injectable } from '@angular/core';
import { DeviceConfigProfileResponse, DeviceConfigProfileCreateRequest, DeviceConfigProfileUpdateRequest } from '@key-telematics/fleet-api-client';
import { AdminEditorService } from '../../admin.service';
import { GridColumnHelper, EnumToComboValues } from '../utils';
import { DeviceConfigProfileDetailsSectionHandler } from './details.section';
import { ICONS } from '../icons';
import { RegisterEntityHandler } from '../../entities';
import { DeviceDetailsSectionHandler } from '../device/details.section';
import { DeviceParametersSectionHandler, getDeviceTypeIdFromBulk } from '../device/parameters.section';
import { FormBuilderDefinition } from 'app/shared/components/form-builder';
import { CustomFormGroupSectionHandler } from '../shared/form.section';
import { DeviceIOWhitelistSectionHandler } from '../device/io-whitelist.section';
import { DeviceIoSectionHandler, InputTypeValues } from '../device/io.section';
import { AccessorySectionHandler } from '../device/accessory.section';
import { DeviceBitmapsSectionHandler } from '../device/bitmaps.section';

@RegisterEntityHandler('deviceconfigprofile')
@Injectable()
export class DeviceConfigProfileHandler extends EntityHandler<DeviceConfigProfileResponse> {

    constructor(
        admin: AdminEditorService
    ) {
        super(admin);
    }

    states = ['active', 'deleted'];

    listEntities(ownerId: string, offset: number, limit: number, sort: string, filter: string): Promise<EntityListResult> {
        return this.admin.getCached(`listDeviceConfigProfiles/${ownerId}/${offset}/${limit}/${sort}/${filter}`, api => api.entities.listDeviceConfigProfiles(ownerId, false, offset, limit, sort, filter), 3).then((response: EntityListResult) => ({
            ...response,
            type: 'deviceconfigprofile',
            title: this.admin.i18n.instant('ADMIN.ENTITIES.DEVICECONFIGPROFILES'),
        }));
    }

    getEntity(id: string): Promise<DeviceConfigProfileResponse> {
        return this.admin.getCached(`getDeviceConfigProfile/${id}`, api => api.entities.getDeviceConfigProfile(id), 10);
    }

    createEntity(ownerId: string, entity: DeviceConfigProfileCreateRequest): Promise<DeviceConfigProfileResponse> {
        entity.ownerId = ownerId;
        return this.admin.applyUpdate(api => api.entities.createDeviceConfigProfile(entity));
    }

    setEntityState(entity: any, state: string): Promise<any> {
        return this.admin.applyUpdate(api => api.entities.updateDeviceConfigProfile(entity.id, { state: state as any }));
    }

    getGridIcon(_item: DeviceConfigProfileResponse): string {
        return ICONS.deviceconfigprofile;
    }

    async getGridColumns(ownerId: string): Promise<IKuiTableCol[]> {

        const company = await this.admin.getCached(`getCompany/${ownerId}`, api => api.accounts.getCompany(ownerId));
        return Promise.all([
            this.admin.getCached(`listDeviceTypes/${ownerId}`, api => api.entities.listDeviceTypes(ownerId)),
            company.type === 'client'
                ? DeviceDetailsSectionHandler.getAvailableRootDeviceTypeIdsForClient(this.admin, ownerId)
                : DeviceDetailsSectionHandler.getAvailableRootDeviceTypeIdsForVendor(this.admin, ownerId)
        ]).then(([deviceTypes, available]) => {

            const deviceTypeValues = DeviceDetailsSectionHandler.filterAllowedDeviceTypes(ownerId, deviceTypes.items, available, []).map(({ key, value }) => ({ id: key, value }));

            const grid = new GridColumnHelper(this.admin.i18n, 'ADMIN.EDITORS.DEVICECONFIGPROFILE.COLUMNS');
            const columns: IKuiTableCol[] = [
                grid.IconTextColumn('name', 'NAME', this.getGridIcon),
                grid.IdNameComboColumn('deviceType', 'DEVICE_TYPE', deviceTypeValues, 100),
                grid.ComboColumn('state', 'STATE', EnumToComboValues(this.admin.i18n, 'ADMIN.STATES', this.states, ['deleted']), 80),
                grid.TimeAgoColumn('modifiedDate', 'MODIFIED'),
            ];
            return Promise.resolve(columns);

        });
    }

    async getEditorSections(ownerId: string, entity: any): Promise<SectionHandlerGroup<DeviceConfigProfileResponse>[]> {

        let custom = [];
        const deviceTypeId = getDeviceTypeIdFromBulk(entity ? [entity] : this.admin.selectedItems);
        const deviceType = deviceTypeId ? await this.admin.getCached(`getDeviceType/${deviceTypeId}/${ownerId}`, api => api.entities.getDeviceType(deviceTypeId, ownerId)) : null;
        if (deviceType && deviceType.settingsDefinition) {
            try {
                const def = JSON.parse(deviceType.settingsDefinition) as FormBuilderDefinition;
                custom = def.groups.map(group => new CustomFormGroupSectionHandler(this.admin, ownerId, group, 'settings', (e, payload) => {
                    return this.admin.applyUpdate(api => api.entities.updateDeviceConfigProfile(e.id, payload));
                }));
            } catch (err) {
                console.warn('Invalid device type settings definition', deviceType.settingsDefinition);
                console.error(err);
            }
        }

        const applyUpdate = (entity: DeviceConfigProfileResponse, payload: DeviceConfigProfileUpdateRequest): Promise<DeviceConfigProfileResponse> => {
            return this.admin.applyUpdate(api => api.entities.updateDeviceConfigProfile(entity.id, payload));
        }

        const sections: SectionHandlerGroup<DeviceConfigProfileResponse>[] = [{
            name: this.admin.i18n.instant('ADMIN.EDITOR.INFORMATION'),
            sections: [
                new DeviceConfigProfileDetailsSectionHandler(this.admin, ownerId, false),
                new DeviceParametersSectionHandler(this.admin, ownerId, applyUpdate.bind(this)),
                ...custom,
            ],
            message: !deviceType ? this.admin.i18n.instant('ADMIN.EDITORS.DEVICE.MESSAGES.NO_BULK') : null,
        }, {
            name: this.admin.i18n.instant('ADMIN.EDITOR.IO'),
            sections: [
                new DeviceIOWhitelistSectionHandler(this.admin, ownerId, applyUpdate.bind(this)),
                new DeviceBitmapsSectionHandler(this.admin, ownerId, applyUpdate.bind(this)),
                ...InputTypeValues.map(iotype => new DeviceIoSectionHandler(this.admin, ownerId, iotype, applyUpdate.bind(this))),
            ],
            message: !deviceType ? this.admin.i18n.instant('ADMIN.EDITORS.DEVICE.MESSAGES.NO_BULK') : null,
        }];

        if (deviceType && deviceType.accessories && Object.keys(deviceType.accessories).length > 0) {

            sections.push({
                name: this.admin.i18n.instant('ADMIN.EDITOR.ACCESSORIES'),
                sections: Object.keys(deviceType.accessories).map(accId => new AccessorySectionHandler(this.admin, ownerId, accId, deviceType.accessories[accId], applyUpdate.bind(this))).sort((a, b) => a.title.localeCompare(b.title)),
                message: undefined,
            });
        }

        return sections;


    }

    getCreationSections(ownerId: string): Promise<{ entityType: string, entity: DeviceConfigProfileUpdateRequest, sections: FormSectionHandler<DeviceConfigProfileResponse, any>[] }> {
        return Promise.resolve({
            entityType: 'deviceconfigprofile',
            entity: {}, // set up some defaults if you want
            sections: [
                new DeviceConfigProfileDetailsSectionHandler(this.admin, ownerId, true),
            ],
        });
    }

}












